import { useState, useEffect, useRef } from 'react'
import { Search, X, Filter, ArrowRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation } from 'react-query'
import { searchAPI } from '../../lib/api'
import { SearchResults } from './SearchResults'
import { useDebounce } from '../../hooks/useDebounce'

interface SearchBarProps {
  value?: string
  onChange?: (value: string) => void
  autoFocus?: boolean
}

export function SearchBar({ value = '', onChange, autoFocus }: SearchBarProps) {
  const [query, setQuery] = useState(value)
  const [showResults, setShowResults] = useState(false)
  const [searchType, setSearchType] = useState<'hybrid' | 'semantic' | 'fuzzy' | 'exact'>('hybrid')
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedQuery = useDebounce(query, 300)

  // Get suggestions
  const { data: suggestions } = useQuery(
    ['suggestions', debouncedQuery],
    () => searchAPI.getSuggestions(debouncedQuery),
    {
      enabled: debouncedQuery.length >= 2,
    }
  )

  // Perform search
  const searchMutation = useMutation(
    (params: { query: string; type: string }) => 
      searchAPI.search(params.query, params.type),
    {
      onSuccess: () => {
        setShowResults(true)
      }
    }
  )

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      searchMutation.mutate({ query: debouncedQuery, type: searchType })
    } else {
      setShowResults(false)
    }
  }, [debouncedQuery, searchType])

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery)
    onChange?.(newQuery)
  }

  const handleClear = () => {
    setQuery('')
    onChange?.('')
    setShowResults(false)
    inputRef.current?.focus()
  }

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
    onChange?.(suggestion)
    searchMutation.mutate({ query: suggestion, type: searchType })
  }

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Suchen Sie nach Dokumenten, Richtlinien, Formularen..."
          autoFocus={autoFocus}
          className="w-full pl-12 pr-24 py-4 bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-gray-400 transition-all"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
          {/* Search Type Selector */}
          <div className="relative">
            <button
              className="p-2 hover:bg-white/10 rounded-lg transition-smooth"
              title="Suchtyp ändern"
            >
              <Filter className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Clear button */}
          {query && (
            <button
              onClick={handleClear}
              className="p-2 hover:bg-white/10 rounded-lg transition-smooth"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Suggestions */}
      <AnimatePresence>
        {suggestions?.suggestions && suggestions.suggestions.length > 0 && !showResults && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 glass rounded-xl overflow-hidden z-10"
          >
            <div className="p-2">
              <p className="text-xs text-gray-500 px-3 py-1">Vorschläge</p>
              {suggestions.suggestions.map((suggestion: string, index: number) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-3 py-2 hover:bg-white/10 rounded-lg transition-smooth flex items-center justify-between group"
                >
                  <span>{suggestion}</span>
                  <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-white opacity-0 group-hover:opacity-100 transition-all" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Results */}
      <AnimatePresence>
        {showResults && searchMutation.data && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mt-6"
          >
            <SearchResults
              results={searchMutation.data.results}
              queryTime={searchMutation.data.query_time_ms}
              totalCount={searchMutation.data.total_count}
              onClose={() => setShowResults(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {searchMutation.isLoading && (
        <div className="absolute top-full left-0 right-0 mt-2 glass rounded-xl p-8 text-center">
          <div className="inline-flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-400">Suche läuft...</span>
          </div>
        </div>
      )}
    </div>
  )
}